import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { AiInstruction } from 'src/state/stores/app'

dayjs.extend(utc)

// Backend timestamps arrive as naive UTC strings (repo convention, see the
// server's `_dto_timestamp`): they must be parsed as UTC. `new Date(...)`
// would read them as *local* time and make every grant look expired for
// anyone east of UTC.
const expiresAtMs = (expiresAt: string): number => dayjs.utc(expiresAt).valueOf()

/**
 * Remaining time of a grant, compact for the panel row: "47 Min", "2:41 h",
 * "abgelaufen". `nowMs` comes from the ticking state in the popover so every
 * row re-renders off the same clock.
 */
export const formatRemainingTime = (expiresAt: string, nowMs: number): string => {
  const remainingMs = expiresAtMs(expiresAt) - nowMs
  if (remainingMs <= 0) return 'abgelaufen'
  const totalMinutes = Math.ceil(remainingMs / 60_000)
  if (totalMinutes < 60) return `${totalMinutes} Min`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}:${String(minutes).padStart(2, '0')} h`
}

export const isExpired = (expiresAt: string, nowMs: number): boolean =>
  expiresAtMs(expiresAt) <= nowMs

/** Shown in the Instruktionen list rows: the first characters of the note. */
export const INSTRUCTION_PREVIEW_LENGTH = 15

export const buildInstructionPreview = (text: string): string =>
  text.length > INSTRUCTION_PREVIEW_LENGTH ? `${text.slice(0, INSTRUCTION_PREVIEW_LENGTH)}…` : text

/**
 * The hand-off text for the AI agent: all instructions of a project, each with
 * the affected elements captured when the note was taken, separated by "-".
 */
export const buildInstructionsClipboardText = (instructions: AiInstruction[]): string => {
  const blocks = instructions.map((instruction, index) => {
    const lines = [`Instruktion #${index + 1}`, '']

    if (instruction.elements.length > 0) {
      lines.push('Betroffene Elemente:', '')
      for (const element of instruction.elements) {
        const elementType = element.elementTypeLabel ?? 'unbekannt'
        lines.push(`* ${element.label} (element-typ: ${elementType}, id: ${element.guid})`)
      }
      lines.push('', '')
    }

    lines.push('Instruktion des Nutzers:', instruction.text)

    return lines.join('\n')
  })

  return [
    'Bitte bearbeite die folgenden Instruktionen, wenn es Probleme gibt dann lass es mich wissen',
    '',
    blocks.join('\n\n-\n\n'),
  ].join('\n')
}
